import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent } from '@/components/ui/card';
import { WorkspaceSurface } from "@/components/ui/workspace-surface";
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  Search,
  BookOpen,
  Users,
  Settings,
  FileText,
  Calculator,
  Plus,
  ArrowRight,
  HelpCircle,
  ChevronDown,
  ChevronUp,
  Database,
  Shield,
  RefreshCw,
  Sparkles,
  LayoutDashboard,
  ArrowLeft,
  Receipt,
  BadgeCent,
  Gavel
} from 'lucide-react';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import { cn } from '@/lib/utils';

const GuidePage = () => {
  const navigate = useNavigate();
  const [searchTerm, setSearchTerm] = useState('');
  const [expandedCategory, setExpandedCategory] = useState<string | null>('Getting Started');

  const faqData = [
    {
      category: 'Getting Started',
      icon: <BookOpen className="h-5 w-5 text-emerald-500" />,
      description: 'The foundation of creating premium estimates.',
      questions: [
        {
          q: 'How do I create a new quotation?',
          a: 'Click the "New Quote" button on the dashboard. Fill in client details, select the estimate type (Interiors, Technical, etc.), and assign a BDM. The system uses standardized DCode numbering: DCODEHS-MMM-YY-XXXX.'
        },
        {
          q: 'What are the different quotation statuses?',
          a: 'Draft: Active work-in-progress. Revised: Finalized version; any further changes create a new version (v1, v2). Shared: Collaborators can view or edit based on roles.'
        },
        {
          q: 'Is my data saved automatically?',
          a: 'Yes, the engine uses a Hybrid Persistence model. Changes are saved locally instantly and synchronized with the cloud database in the background.'
        }
      ]
    },
    {
      category: 'Billing & Invoicing',
      icon: <Receipt className="h-5 w-5 text-blue-400" />,
      description: 'Managing revenue and tax compliance.',
      questions: [
        {
          q: 'How do I generate an invoice?',
          a: 'Open any finalized quotation and click "Issue Invoice". The system will automatically map rooms and items into a professional tax invoice format.'
        },
        {
          q: 'When is an Invoice Number assigned?',
          a: 'To maintain perfect accounting records, sequential invoice numbers are only assigned when a draft is "Issued". Drafts remain as "[DRAFT]" and do not consume numbers.'
        },
        {
          q: 'Does editing an invoice affect the estimate?',
          a: 'No. Once an invoice is generated, it becomes an independent financial record. You can edit line items, rates, and addresses within the Invoice Wizard without affecting the original engineering estimate.'
        },
        {
          q: 'What is the Business Intelligence Dashboard?',
          a: 'The revamped Dashboard displays your entire financial pipeline: Collective Valuation (potential revenue from all quotes), Total Invoiced (receivables), and Revenue Received (actual cash collected). Use it to track conversion rates and collection health.'
        }
      ]
    },
    {
      category: 'Accounting & Ledger',
      icon: <Gavel className="h-5 w-5 text-amber-400" />,
      description: 'Chart of accounts, vouchers, and financial reports.',
      questions: [
        {
          q: 'How do I add a new ledger group?',
          a: 'Go to Accounting → Ledgers and click "New Group". Enter the group name, select its nature (Assets, Liabilities, Income, Expenses, Equity), and optionally set a parent group and sort order.'
        },
        {
          q: 'Can I create a party while posting a voucher?',
          a: 'Yes. In the voucher entry form, select the Party Ledger dropdown and choose "+ Create New Party". Fill in the party name, type, and opening balance to add the ledger inline.'
        },
        {
          q: 'What reports are available in Accounting?',
          a: 'The module includes Dashboard, Vouchers, Ledgers, Trial Balance, Profit & Loss, Balance Sheet, GST Report, and ledger statement drill-downs.'
        },
        {
          q: 'How does voucher posting work?',
          a: 'Each voucher uses double-entry accounting. Debit and credit totals must balance before posting, and posted vouchers are used to generate final financial reports.'
        }
      ]
    },
    {
      category: 'Catalog Management',
      icon: <Database className="h-5 w-5 text-blue-500" />,
      description: 'Master your items, pricing, and specifications.',
      questions: [
        {
          q: 'What is the Master Registry?',
          a: 'A centralized database of verified items, materials, and labor costs. Using registry items ensures pricing consistency across all projects.'
        },
        {
          q: 'How do I add new items to the registry?',
          a: 'Navigate to Settings → Catalog. You can add items with specific dimensions, categories, and base rates. These become available to everyone in the team.'
        },
        {
          q: 'Can I import items from an existing quote?',
          a: 'Yes! Items modified within a specific quote can be "Committed to Registry" via the Catalog Manager, making them available for future use.'
        }
      ]
    },
    {
      category: 'Advanced Calculations',
      icon: <Calculator className="h-5 w-5 text-amber-500" />,
      description: 'Understanding the DCODE estimation logic.',
      questions: [
        {
          q: 'How does the Area Calculation work?',
          a: 'The system uses the DCode Formula: (Length × Width) ÷ 900 for CM inputs. Results are rounded up to the nearest integer to account for material wastage.'
        },
        {
          q: 'How is GST and Discount applied?',
          a: 'Discounts are applied to the Subtotal first. GST (default 18%) is then calculated on the discounted amount. You can override global GST in the Quotation Engine settings.'
        },
        {
          q: 'What is the Payment Milestone engine?',
          a: 'It automatically distributes the Total Value across standard phases (Booking, Design, Material, Execution). Percentages must total 100% to validate the estimate.'
        }
      ]
    },
    {
      category: 'Team & Collaboration',
      icon: <Users className="h-5 w-5 text-purple-500" />,
      description: 'Working together on large-scale projects.',
      questions: [
        {
          q: 'How do I share a quotation?',
          a: 'Each quote has a "Share" feature. You can invite team members by email and assign roles like "Editor", "Viewer", or "Admin".'
        },
        {
          q: 'What can different roles do?',
          a: 'Admins: Full control including deletion. Editors: Can modify items and pricing. Viewers: Read-only access, ideal for management review.'
        },
        {
          q: 'Tracking revisions in a team?',
          a: 'The "History" tab shows a detailed audit trail of who changed what and when. You can restore any previous snapshot if a collaborator makes a mistake.'
        }
      ]
    },
    {
      category: 'Customization & Branding',
      icon: <Sparkles className="h-5 w-5 text-rose-500" />,
      description: 'Tailoring the output for your clients.',
      questions: [
        {
          q: 'How do I update Company Identity?',
          a: 'Go to Settings → Company. Update your logo, address, and legal details. These are used in "Full Branded Quote" printouts.'
        },
        {
          q: 'Can I edit the default Terms & Conditions?',
          a: 'Yes, in Settings → Engine. You can define global placeholders for Inclusions, Exclusions, and Warranty clauses that appear on every new estimate.'
        },
        {
          q: 'What are the "Print Modules"?',
          a: 'Letterhead Print: Clean list for official letterheads. Full Branded: Comprehensive layout with T&Cs. Client Copy: Masked rates for initial design discussions.'
        }
      ]
    },
    {
      category: 'Troubleshooting',
      icon: <HelpCircle className="h-5 w-5 text-muted-foreground" />,
      description: 'Resolving common issues and sync errors.',
      questions: [
        {
          q: 'Quotation is showing "Sync Pending"?',
          a: 'This means your changes are saved locally but not yet pushed to the cloud. Check your connection; it will auto-retry once online.'
        },
        {
          q: 'Cannot delete a shared quotation?',
          a: 'Only the "Owner" or an "Admin" has permission to delete items. If you are an Editor, you can only modify content.'
        },
        {
          q: 'Missing item images in print?',
          a: 'Ensure you have assigned a valid image URL in the Catalog Registry. High-resolution images are recommended for professional PDF outputs.'
        }
      ]
    }
  ];

  const filteredCategories = faqData.filter(category =>
    category.category.toLowerCase().includes(searchTerm.toLowerCase()) ||
    category.questions.some(q =>
      q.q.toLowerCase().includes(searchTerm.toLowerCase()) ||
      q.a.toLowerCase().includes(searchTerm.toLowerCase())
    )
  );

  return (
    <div className="min-h-screen bg-zinc-50 dark:bg-zinc-950 p-4 md:p-8 animate-fade-in acc-shell">
      <div className="relative z-10 max-w-5xl mx-auto space-y-12">
        {/* Navigation */}
        <div className="flex justify-start">
          <Button 
            variant="ghost" 
            onClick={() => navigate('/')}
            className="text-zinc-500 hover:text-zinc-900 dark:hover:text-zinc-100 hover:bg-zinc-100 dark:hover:bg-zinc-900 font-medium text-xs group"
          >
            <ArrowLeft className="h-4 w-4 mr-2 transition-transform group-hover:-translate-x-1" />
            Back to Workspace
          </Button>
        </div>

        {/* Header Section */}
        <div className="text-center space-y-4">
          <h1 className="text-4xl md:text-5xl font-semibold text-zinc-900 dark:text-zinc-100 tracking-tight leading-none">
            Intelligence Guide
          </h1>
          <p className="text-zinc-500 text-base md:text-lg max-w-2xl mx-auto font-medium leading-relaxed">
            The ultimate blueprint for mastering the DCODE estimation ecosystem.
            Synchronize your workflow with enterprise-grade precision.
          </p>
        </div>

        {/* Search Bar */}
        <div className="relative max-w-2xl mx-auto group">
          <div className="relative flex items-center bg-white dark:bg-zinc-950 border border-zinc-200 dark:border-white/5 rounded-xl px-6 h-14 shadow-sm focus-within:border-emerald-500/30 transition-all">
            <Search className="h-5 w-5 text-emerald-500 mr-4" />
            <Input
              placeholder="Search features, logic or troubleshooting..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="bg-transparent border-0 focus-visible:ring-0 text-zinc-900 dark:text-zinc-100 font-medium text-base placeholder:text-zinc-500 w-full"
            />
          </div>
        </div>

        {/* FAQ Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-4 gap-8">
          {/* Navigation Sidebar */}
          <div className="lg:col-span-1 space-y-2">
            <p className="text-[10px] font-medium text-zinc-500 uppercase tracking-[0.08em] mb-4 ml-2">Categories</p>
            {faqData.map((cat) => (
              <button
                key={cat.category}
                onClick={() => setExpandedCategory(cat.category)}
                className={cn(
                  "w-full text-left px-4 py-2.5 rounded-lg transition-all font-medium text-sm flex items-center justify-between group",
                  expandedCategory === cat.category
                    ? "bg-zinc-200/50 dark:bg-zinc-800 text-zinc-900 dark:text-zinc-100 shadow-sm"
                    : "text-zinc-500 hover:bg-zinc-100 dark:hover:bg-zinc-900/50 hover:text-zinc-900 dark:hover:text-zinc-100"
                )}
              >
                <div className="flex items-center gap-3">
                  {cat.icon}
                  {cat.category}
                </div>
                <ArrowRight className={cn("h-4 w-4 transition-transform", expandedCategory === cat.category ? "translate-x-0" : "-translate-x-2 opacity-0 group-hover:opacity-100 group-hover:translate-x-0")} />
              </button>
            ))}
          </div>

          {/* Content Area */}
          <div className="lg:col-span-3 space-y-6">
            {filteredCategories.filter(c => !expandedCategory || c.category === expandedCategory).map((category, index) => (
              <div key={index} className="space-y-6 animate-in fade-in slide-in-from-bottom-4 duration-500">
                <div className="flex items-center gap-4 border-b border-zinc-200 dark:border-white/5 pb-6">
                  <div className="h-12 w-12 rounded-xl bg-zinc-100 dark:bg-zinc-800 flex items-center justify-center border border-zinc-200 dark:border-white/5 shadow-sm">
                    {category.icon}
                  </div>
                  <div>
                    <h2 className="text-xl font-semibold text-zinc-900 dark:text-zinc-100 tracking-tight">{category.category}</h2>
                    <p className="text-zinc-500 text-sm font-medium mt-1">{category.description}</p>
                  </div>
                </div>

                <div className="space-y-4">
                  {category.questions.map((item, qIndex) => (
                    <WorkspaceSurface key={qIndex} className="bg-white dark:bg-zinc-900/50 border-zinc-200 dark:border-white/5 hover:border-emerald-500/30 transition-all group overflow-hidden shadow-sm">
                      <Collapsible defaultOpen={qIndex === 0}>
                        <CollapsibleTrigger className="w-full">
                          <div className="flex items-center justify-between p-5 text-left hover:bg-zinc-50 dark:hover:bg-zinc-900 transition-colors">
                            <div className="flex gap-4 items-center">
                              <span className="text-[10px] font-medium text-emerald-600 dark:text-emerald-500 bg-emerald-50 dark:bg-emerald-500/10 h-6 w-6 rounded-md flex items-center justify-center shrink-0">
                                {qIndex + 1}
                              </span>
                              <h3 className="font-semibold text-zinc-900 dark:text-zinc-100 text-sm tracking-tight pr-8">
                                {item.q}
                              </h3>
                            </div>
                            <ChevronDown className="h-5 w-5 text-zinc-400 group-hover:text-emerald-500 transition-colors" />
                          </div>
                        </CollapsibleTrigger>
                        <CollapsibleContent className="border-t border-zinc-100 dark:border-white/5">
                          <CardContent className="p-5 bg-zinc-50 dark:bg-zinc-950">
                            <p className="text-zinc-600 dark:text-zinc-400 text-sm leading-relaxed font-medium">
                              {item.a}
                            </p>
                          </CardContent>
                        </CollapsibleContent>
                      </Collapsible>
                    </WorkspaceSurface>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Global Stats Footer */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 pt-12 border-t border-zinc-200 dark:border-white/5">
          {[
            { label: 'Latency', value: '42ms', icon: RefreshCw },
            { label: 'Accuracy', value: '100%', icon: Shield },
            { label: 'Uptime', value: '99.99%', icon: LayoutDashboard },
            { label: 'Support', value: '24/7', icon: HelpCircle }
          ].map((stat, i) => (
            <div key={i} className="bg-white dark:bg-zinc-900/50 border border-zinc-200 dark:border-white/5 p-6 rounded-xl text-center shadow-sm">
              <stat.icon className="h-5 w-5 text-emerald-500 mx-auto mb-3" />
              <p className="text-2xl font-semibold tabular-nums text-zinc-900 dark:text-zinc-100 leading-none">{stat.value}</p>
              <p className="text-[10px] font-medium text-zinc-500 uppercase tracking-[0.08em] mt-2">{stat.label}</p>
            </div>
          ))}
        </div>

        {/* CTA Section */}
        <div className="bg-emerald-600 rounded-2xl p-8 md:p-12 text-center space-y-4 shadow-sm overflow-hidden relative">
          <h2 className="text-2xl md:text-3xl font-semibold text-white tracking-tight relative z-10">
            Still Have Questions?
          </h2>
          <p className="text-emerald-50 text-base md:text-lg max-w-xl mx-auto relative z-10">
            Our enterprise architects are available for a deep-dive session to optimize your team's estimation workflow.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center pt-4 relative z-10">
            <Button className="h-12 px-8 rounded-lg bg-white text-emerald-600 hover:bg-zinc-100 font-semibold text-sm shadow-sm transition-all">
              Contact Tech Support
            </Button>
            <Button variant="outline" className="h-12 px-8 rounded-lg border-white/20 text-white hover:bg-white/10 font-semibold text-sm transition-all">
              Request Demo
            </Button>
          </div>
        </div>
      </div>

      <footer className="mt-20 text-center pb-12">
        <p className="text-zinc-400 font-medium text-[10px] uppercase tracking-[0.2em]">
          DCODE PRIVATE LIMITED • EST. 2012
        </p>
      </footer>
    </div>
  );
};

export default GuidePage;
